import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

/**
 * The ClientMa component set.
 *
 * Kept deliberately small: the pieces every screen needs, each doing one thing. Anything a
 * single screen needs belongs in that screen, not here — a design system that grows to cover
 * every case stops being learnable.
 */

// ══════════════════════════════════════════════════════════════════ Button

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  /** Shows a spinner and disables the button, so a double submit is impossible. */
  isLoading?: boolean;
  block?: boolean;
  iconOnly?: boolean;
  leadingIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    isLoading = false,
    block = false,
    iconOnly = false,
    leadingIcon,
    className = '',
    children,
    disabled,
    ...rest
  },
  ref,
) {
  const classes = [
    'cma-btn',
    `cma-btn--${variant}`,
    size !== 'md' ? `cma-btn--${size}` : '',
    block ? 'cma-btn--block' : '',
    iconOnly ? 'cma-btn--icon' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={ref}
      className={classes}
      disabled={disabled || isLoading}
      // Tells assistive technology the control is busy rather than simply unresponsive.
      aria-busy={isLoading || undefined}
      {...rest}
    >
      {isLoading ? <span className="cma-btn__spinner" aria-hidden="true" /> : leadingIcon}
      {children}
    </button>
  );
});

// ════════════════════════════════════════════════════════════════════ Card

export interface CardProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  flush?: boolean;
  className?: string;
  children: ReactNode;
}

export function Card({
  title,
  subtitle,
  actions,
  footer,
  flush = false,
  className = '',
  children,
}: CardProps) {
  return (
    <section className={`cma-card ${className}`}>
      {(title || actions) && (
        <header className="cma-card__header">
          <div>
            {title && <h2 className="cma-card__title">{title}</h2>}
            {subtitle && <p className="cma-card__subtitle">{subtitle}</p>}
          </div>
          {actions && <div className="cma-row">{actions}</div>}
        </header>
      )}

      <div className={`cma-card__body${flush ? ' cma-card__body--flush' : ''}`}>{children}</div>

      {footer && <footer className="cma-card__footer">{footer}</footer>}
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════ Stat

export interface StatProps {
  label: string;
  value: ReactNode;
  /** Signed change against the comparison period. */
  delta?: number | null;
  deltaLabel?: string;
  /** For a metric where a fall is good — days to close, cost per lead. */
  invertDelta?: boolean;
  meta?: ReactNode;
  tone?: 'default' | 'success' | 'warning' | 'danger';
}

export function Stat({
  label,
  value,
  delta,
  deltaLabel,
  invertDelta = false,
  meta,
  tone = 'default',
}: StatProps) {
  const isUp = (delta ?? 0) > 0;
  const isGood = invertDelta ? !isUp : isUp;

  const valueColor =
    tone === 'success'
      ? 'var(--success)'
      : tone === 'warning'
        ? 'var(--warning)'
        : tone === 'danger'
          ? 'var(--danger)'
          : undefined;

  return (
    <div className="cma-stat">
      <span className="cma-stat__label">{label}</span>
      <span className="cma-stat__value" style={valueColor ? { color: valueColor } : undefined}>
        {value}
      </span>

      {(delta !== undefined && delta !== null) || meta ? (
        <span className="cma-stat__meta">
          {delta !== undefined && delta !== null && (
            <span className={`cma-stat__delta cma-stat__delta--${isGood ? 'up' : 'down'}`}>
              {isUp ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%
            </span>
          )}
          {deltaLabel && <span>{deltaLabel}</span>}
          {meta}
        </span>
      ) : null}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════ Badge

export type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

export function Badge({
  tone = 'neutral',
  dot = false,
  title,
  children,
}: {
  tone?: BadgeTone;
  dot?: boolean;
  /** Tooltip explaining an abbreviated label — a badge has room for two words at most. */
  title?: string;
  children: ReactNode;
}) {
  return (
    <span className={`cma-badge cma-badge--${tone}`} title={title}>
      {dot && <span className="cma-badge__dot" aria-hidden="true" />}
      {children}
    </span>
  );
}

/**
 * Maps a domain status onto a tone. Centralised so "Won" is green in every screen that shows
 * it, rather than green here and neutral three screens away.
 */
export function toneForStatus(status: string | null | undefined): BadgeTone {
  const value = (status ?? '').toLowerCase();

  if (['won', 'active', 'completed', 'delivered', 'approved', 'confirmed', 'achieved', 'clean', 'succeeded', 'on_track']
      .includes(value)) return 'success';

  if (['lost', 'cancelled', 'rejected', 'failed', 'declined', 'expired', 'infected', 'behind', 'creditHold', 'credithold']
      .includes(value)) return 'danger';

  if (['pending', 'pendingapproval', 'draft', 'snoozed', 'at_risk', 'partiallyfulfilled', 'inreview', 'onhold', 'uncertain']
      .includes(value)) return 'warning';

  if (['sent', 'viewed', 'inprogress', 'scheduled', 'open', 'new', 'delivered'].includes(value)) return 'info';

  return 'neutral';
}

// ═══════════════════════════════════════════════════════════════════ Fields

export interface FieldProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: (id: string) => ReactNode;
}

/**
 * Wraps a control with its label, hint and error, wiring the ids so the label points at the
 * control and screen readers announce the error with it.
 */
export function Field({ label, hint, error, required, children }: FieldProps) {
  const id = useId();
  const describedBy = [hint ? `${id}-hint` : null, error ? `${id}-error` : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="cma-field">
      {label && (
        <label className="cma-field__label" htmlFor={id}>
          {label}
          {required && (
            <span className="cma-field__required" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}

      <div aria-describedby={describedBy || undefined}>{children(id)}</div>

      {hint && !error && (
        <span className="cma-field__hint" id={`${id}-hint`}>
          {hint}
        </span>
      )}

      {error && (
        <span className="cma-field__error" id={`${id}-error`} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, className = '', ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      className={`cma-input${invalid ? ' cma-input--invalid' : ''} ${className}`}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  );
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid, className = '', children, ...rest },
  ref,
) {
  return (
    <select
      ref={ref}
      className={`cma-select${invalid ? ' cma-select--invalid' : ''} ${className}`}
      aria-invalid={invalid || undefined}
      {...rest}
    >
      {children}
    </select>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, className = '', ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      className={`cma-textarea${invalid ? ' cma-textarea--invalid' : ''} ${className}`}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  );
});

// ═══════════════════════════════════════════════════════════════ Feedback

export function Alert({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'success' | 'warning' | 'danger';
  title?: string;
  children?: ReactNode;
}) {
  return (
    <div className={`cma-alert cma-alert--${tone}`} role={tone === 'danger' ? 'alert' : 'status'}>
      <div>
        {title && <div className="cma-alert__title">{title}</div>}
        {children && <div>{children}</div>}
      </div>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon?: ReactNode;
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="cma-empty">
      {icon && <div className="cma-empty__icon">{icon}</div>}
      <div className="cma-empty__title">{title}</div>
      {body && <p className="cma-empty__body">{body}</p>}
      {action}
    </div>
  );
}

export function Skeleton({
  width = '100%',
  height = 16,
  radius,
}: {
  width?: string | number;
  height?: string | number;
  radius?: string;
}) {
  return (
    <div
      className="cma-skeleton"
      style={{ width, height, borderRadius: radius }}
      aria-hidden="true"
    />
  );
}

/** A few skeleton rows, for a table or list that is loading. */
export function SkeletonRows({ rows = 5 }: { rows?: number }) {
  return (
    <div className="cma-stack cma-stack--tight" style={{ padding: 'var(--space-4)' }}>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} height={20} />
      ))}
    </div>
  );
}

export function Progress({
  value,
  max = 100,
  tone,
  /** Draws a marker at this percentage — used to show elapsed period against attainment. */
  marker,
  label,
}: {
  value: number;
  max?: number;
  tone?: 'success' | 'warning' | 'danger';
  marker?: number;
  label?: string;
}) {
  const percent = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;

  return (
    <div
      className="cma-progress"
      role="progressbar"
      aria-valuenow={Math.round(percent)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div
        className={`cma-progress__fill${tone ? ` cma-progress__fill--${tone}` : ''}`}
        style={{ width: `${percent}%` }}
      />
      {marker !== undefined && marker > 0 && marker < 100 && (
        <div className="cma-progress__marker" style={{ left: `${marker}%` }} title="Period elapsed" />
      )}
    </div>
  );
}

export function Avatar({
  name,
  size = 'md',
}: {
  name: string | null | undefined;
  size?: 'sm' | 'md' | 'lg';
}) {
  const initials = (name ?? '?')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

  return (
    <span
      className={`cma-avatar${size !== 'md' ? ` cma-avatar--${size}` : ''}`}
      title={name ?? undefined}
      aria-hidden="true"
    >
      {initials || '?'}
    </span>
  );
}

// ══════════════════════════════════════════════════════════════ Overlays

export interface DrawerProps {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
}

/**
 * A side panel for detail and edit flows.
 *
 * Escape closes it, focus moves inside on open and the page behind stops scrolling — the
 * three things that make an overlay feel like part of the app rather than an interruption.
 */
export function Drawer({ open, title, onClose, footer, children }: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Focus the panel so the next Tab lands inside it rather than back in the page behind.
    panelRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className="cma-scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        className="cma-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
      >
        <header className="cma-drawer__header">
          <h2 className="cma-card__title">{title}</h2>
          <Button variant="ghost" size="sm" iconOnly onClick={onClose} aria-label="Close">
            ✕
          </Button>
        </header>

        <div className="cma-drawer__body">{children}</div>

        {footer && <footer className="cma-drawer__footer">{footer}</footer>}
      </div>
    </>
  );
}

export interface ModalProps {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  footer?: ReactNode;

  /**
   * 'wide' for content that is a table rather than a question — a report result in the default
   * width is a column of wrapped text nobody can read across.
   */
  size?: 'default' | 'wide';
  children: ReactNode;
}

export function Modal({ open, title, onClose, footer, size = 'default', children }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', onKeyDown);
    panelRef.current?.focus();

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className="cma-scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        className={`cma-modal${size === 'wide' ? ' cma-modal--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
      >
        <header className="cma-card__header">
          <h2 className="cma-card__title">{title}</h2>
        </header>

        <div className="cma-drawer__body">{children}</div>

        {footer && <footer className="cma-drawer__footer">{footer}</footer>}
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════ Tabs

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: T; label: string; count?: number }[];
  active: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="cma-tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          role="tab"
          aria-selected={tab.id === active}
          className={`cma-tab${tab.id === active ? ' is-active' : ''}`}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
          {tab.count !== undefined && (
            <span style={{ marginLeft: 6, opacity: 0.6 }}>{tab.count}</span>
          )}
        </button>
      ))}
    </div>
  );
}
